import { Link } from "react-router-dom";
import type { AdminOpenClaw } from "../../../types/openclaw";
import { formatDateTime } from "../../../utils/formatDateTime";
import { StatusBadge } from "./StatusBadge";
import styles from "./instanceDetail.module.css";

const VersionChip = ({ claw }: { claw: AdminOpenClaw }): JSX.Element | null => {
  const version = claw.health?.runningVersion ?? null;
  if (version == null) return null;
  const outdated = claw.health?.versionOutdated ?? false;
  const installed = claw.health?.installedVersion;
  return (
    <span
      className={outdated ? `${styles.chip} ${styles.chipWarn}` : styles.chip}
      title={outdated ? `Running ${version}, which differs from the fleet pin${installed ? ` (installed ${installed})` : ""}.` : `OpenClaw ${version}`}
    >
      {version}{outdated ? " · outdated" : ""}
    </span>
  );
};

// Admin overrides that change how the platform treats this instance; shown
// here so they are never missed, edited on the Settings tab.
const OverrideChips = ({ claw }: { claw: AdminOpenClaw }): JSX.Element => (
  <>
    {claw.unlimitedEntitlements && <span className={`${styles.chip} ${styles.chipInfo}`}>Unlimited</span>}
    {(claw.bonusSlots ?? 0) > 0 && <span className={`${styles.chip} ${styles.chipInfo}`}>+{claw.bonusSlots} bonus slots</span>}
    {claw.agentAccess === "platform_admin" && <span className={`${styles.chip} ${styles.chipInfo}`}>Platform admin agent</span>}
  </>
);

export const InstanceDetailHeader = ({ claw }: { readonly claw: AdminOpenClaw }): JSX.Element => {
  const created = claw.createdAt ? formatDateTime(claw.createdAt).slice(0, 10) : "";
  return (
    <header className={styles.header}>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/dashboard/admin/instances" className={styles.crumbLink}>Instances</Link>
        <span className={styles.crumbSep} aria-hidden>/</span>
        <span className={styles.crumbCurrent}>{claw.name}</span>
      </nav>

      <div className={styles.headerMain}>
        <div className={styles.identity}>
          <h2 className={styles.title}>{claw.name}</h2>
          <StatusBadge claw={claw} />
          <VersionChip claw={claw} />
          <OverrideChips claw={claw} />
        </div>
        <Link className={styles.headerAction} to={`/dashboard/admin/support?tenantId=${encodeURIComponent(claw.tenantId)}`}>
          Support messages
        </Link>
      </div>

      <p className={styles.meta}>
        <code className={styles.code}>{claw.tenantId}</code>
        <span className={styles.metaSep}>·</span>
        {claw.ownerEmail}
        <span className={styles.metaSep}>·</span>
        <span className={styles.tier}>{claw.tier}</span>
        {created !== "" && (
          <>
            <span className={styles.metaSep}>·</span>
            created {created}
          </>
        )}
      </p>
    </header>
  );
};
